import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Form, required, useLogin, useNotify, useTranslate } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { useConfigurationContext } from "@/components/mimin/root/ConfigurationContext.tsx";
import { AuthShell } from "./AuthShell";
import { PasswordInput } from "./PasswordInput";
import { SSOAuthButton } from "./SSOAuthButton";

/**
 * Login page displayed when authentication is enabled and the user is not authenticated.
 *
 * Automatically shown when an unauthenticated user tries to access a protected route.
 * Handles login via authProvider.login() and displays error notifications on failure.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/loginpage LoginPage documentation}
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/security Security documentation}
 */
export const LoginPage = (props: { redirectTo?: string }) => {
  const { title, googleWorkplaceDomain, disableEmailPasswordAuthentication } =
    useConfigurationContext();
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  const hasDisplayedRecoveryNotification = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldNotify = searchParams.get("passwordRecoveryEmailSent") === "1";
    // Satt av redningsluken i lib/authLenke.ts når Supabase sendte oss hit med
    // en utløpt eller brukt lenke. Uten meldingen ser en utløpt lenke ut som
    // om ingenting skjedde.
    const authFeil = searchParams.get("authFeil");

    if (
      (!shouldNotify && !authFeil) ||
      hasDisplayedRecoveryNotification.current
    ) {
      return;
    }

    hasDisplayedRecoveryNotification.current = true;
    if (authFeil) {
      notify("crm.auth.link_expired", {
        type: "warning",
        messageArgs: {
          _: "Lenken er utløpt eller allerede brukt. Be om en ny for å sette nytt passord.",
        },
      });
    } else {
      notify("crm.auth.recovery_email_sent", {
        type: "success",
        messageArgs: {
          _: "If you're a registered user, you should receive a password recovery email shortly.",
        },
      });
    }

    searchParams.delete("authFeil");
    searchParams.delete("passwordRecoveryEmailSent");
    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, notify]);

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectTo)
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  return (
    <AuthShell
      title={translate("crm.auth.welcome_back", { _: "Velkommen tilbake" })}
      subtitle={translate("crm.auth.sign_in_subtitle", {
        _: `Logg inn for å fortsette til ${title}`,
        title,
      })}
      footer={
        disableEmailPasswordAuthentication ? null : (
          <Link
            to={"/forgot-password"}
            className="text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
          >
            {translate("ra-supabase.auth.forgot_password", {
              _: "Glemt passord?",
            })}
          </Link>
        )
      }
    >
      {googleWorkplaceDomain ? (
        <div className="mb-6">
          <SSOAuthButton className="h-11 w-full" domain={googleWorkplaceDomain}>
            {translate("crm.auth.sign_in_google_workspace", {
              _: "Sign in with Google Workplace",
            })}
          </SSOAuthButton>
          {disableEmailPasswordAuthentication ? null : (
            // Skillelinje med «eller» — vises bare når det faktisk er noe å
            // velge mellom.
            <div className="mt-6 flex items-center gap-3">
              <span className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">
                {translate("crm.auth.or", { _: "eller" })}
              </span>
              <span className="h-px flex-1 bg-border" />
            </div>
          )}
        </div>
      ) : null}

      {disableEmailPasswordAuthentication ? null : (
        <Form className="space-y-5" onSubmit={handleSubmit}>
          <TextInput
            label="ra.auth.email"
            source="email"
            type="email"
            // Uten autoComplete tilbyr hverken nettleseren eller
            // passordboksen det den allerede vet.
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            validate={required()}
            inputClassName="h-11"
          />
          <PasswordInput
            label="ra.auth.password"
            source="password"
            autoComplete="current-password"
            validate={required()}
            inputClassName="h-11"
          />
          <Button
            type="submit"
            size="lg"
            className="h-11 w-full text-base"
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                {translate("crm.auth.signing_in", { _: "Logger inn …" })}
              </>
            ) : (
              translate("ra.auth.sign_in")
            )}
          </Button>
        </Form>
      )}
    </AuthShell>
  );
};
